'use client'

import React, { createContext, useCallback, use, useEffect, useState } from 'react'

import type { Theme, ThemeContextType } from './types'

import canUseDOM from '@/utilities/canUseDOM'
import { defaultTheme, themeLocalStorageKey } from './shared'

const initialContext: ThemeContextType = {
  setTheme: () => null,
  theme: undefined,
}

const ThemeContext = createContext(initialContext)

const applyDarkTheme = (setThemeState: (theme: Theme) => void) => {
  document.documentElement.setAttribute('data-theme', defaultTheme)
  document.documentElement.classList.add('dark')
  window.localStorage.setItem(themeLocalStorageKey, defaultTheme)
  setThemeState(defaultTheme)
}

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const [theme, setThemeState] = useState<Theme | undefined>(
    canUseDOM ? defaultTheme : undefined,
  )

  const setTheme = useCallback((_themeToSet: Theme | null) => {
    // Theme switching is disabled — always keep dark.
    applyDarkTheme(setThemeState)
  }, [])

  useEffect(() => {
    applyDarkTheme(setThemeState)
  }, [])

  return <ThemeContext value={{ setTheme, theme }}>{children}</ThemeContext>
}

export const useTheme = (): ThemeContextType => use(ThemeContext)
